/**
 * Public profile view - comprehensive display of user profile
 * Shows all visible profile data in an attractive, shareable format
 */
import { render, showToast, showApiError, refreshLikesCount } from '../main';
import {
  TOAST_INTEREST_SENT,
  toastUnmatched,
  toastProfileHidden,
  API_ERR_SEND_INTEREST,
  API_ERR_UNMATCH,
  API_ERR_HIDE_PROFILE,
} from '../utils/app-strings';
import { profileApi, searchApi, matchesApi } from '../api';
import type { BrowseProfile, PublicProfileResponse } from '../api';
import { store } from '../state';
import { SKIP_SCORE } from '../config';
import { getRouteParams, navigate } from '../router';
import { showSafetyActionSheet } from '../utils/safety';
import { openImageViewer } from '../components/image-viewer';
import { renderBrowseCard } from '../components/browse-card';
import {
  loadingSpinner,
  emptyState,
  formatFriendlyDate,
  formatHeight,
  formatWeight,
  formatDistance,
  getUnits,
  setupPageFlipGesture,
  calculateAge,
} from '../utils';
import {
  formatLabel,
  formatFrequency,
  formatExercise,
  formatBodyType,
  formatEducation,
  formatEmployment,
  formatIncome,
  formatKidsStatus,
  formatWantsKids,
  formatFlexibility,
  formatPronouns,
  formatRelationshipStructure,
  formatLanguage,
  formatRelationshipIntent,
  formatEthnicities,
  formatPetsList,
  formatZodiacSign,
  filterPnta,
  CHILD_GENDER_LABELS,
  INTERESTED_IN_LABELS,
  RELATIONSHIP_INTENT_LABELS,
} from '../options';
import type { ProfileJson, IntentEntry } from '../options';

// Store the current profile data for actions
let currentUserId: string | null = null;
let currentFirstName: string = '';

// Re-entrancy guard for interaction API calls
let interactionInFlight = false;

// Track whether current profile is matched (for unmatch confirmation)
let currentIsMatched = false;

// Gesture cleanup functions
let gestureCleanups: (() => void)[] = [];

function cleanupGestures(): void {
  gestureCleanups.forEach(cleanup => cleanup());
  gestureCleanups = [];
}

/**
 * Setup close button to navigate back to where the user came from.
 * If browse context exists (came from browse grid), go to the stored returnTo route.
 * Otherwise (came from chat, matches, etc.), use history.back().
 */
function setupProfileCloseButton(): void {
  document.getElementById('close-profile-btn')?.addEventListener('click', () => {
    const context = store.getBrowseContext();
    if (context?.returnTo) {
      navigate(context.returnTo);
    } else {
      navigate('/');
    }
  });
}

/**
 * Navigate to adjacent profile in browse context
 * @param direction -1 for previous, 1 for next
 */
function navigateToProfile(direction: -1 | 1): void {
  const context = store.getBrowseContext();
  if (!context || context.profiles.length === 0) return;

  let newIndex = context.currentIndex + direction;

  // Wrap around
  if (newIndex < 0) {
    newIndex = context.profiles.length - 1;
  } else if (newIndex >= context.profiles.length) {
    newIndex = 0;
  }

  // Update context and navigate
  store.setBrowseContext({ ...context, currentIndex: newIndex });
  const nextProfile = context.profiles[newIndex];
  navigate(`/u/${nextProfile.user_id}`);
}

/**
 * Setup single-click on profile photos to open fullscreen image viewer.
 * Works for all profiles (own and others).
 */
function setupPhotoClickHandlers(): void {
  const wrapper = document.querySelector('.pub-wrapper') as HTMLElement;
  if (!wrapper) return;

  const allImgs: string[] = [];
  const heroImg = wrapper.querySelector('.pub-hero-img') as HTMLImageElement;
  if (heroImg) allImgs.push(heroImg.getAttribute('src')!);
  wrapper.querySelectorAll('.pub-photo-block img').forEach((img) => {
    allImgs.push((img as HTMLImageElement).src);
  });

  if (heroImg) {
    heroImg.style.cursor = 'pointer';
    heroImg.addEventListener('click', () => openImageViewer(allImgs, 0));
  }

  wrapper.querySelectorAll('.pub-photo-block img').forEach((img, i) => {
    (img as HTMLElement).style.cursor = 'pointer';
    img.addEventListener('click', () => openImageViewer(allImgs, i + 1));
  });
}

/**
 * Setup gesture handlers for the profile view
 */
function setupGestures(isMatched: boolean, chatId: string | null, iLiked: boolean = false): void {
  cleanupGestures();

  const wrapper = document.querySelector('.pub-wrapper') as HTMLElement;
  if (!wrapper) return;

  const context = store.getBrowseContext();
  const hasBrowseContext = context && context.profiles.length > 1;

  // Page-flip swipe: interested/not for me (only when not matched)
  if (!isMatched) {
    const flipCleanup = setupPageFlipGesture({
      element: wrapper,
      onFlipLeft: () => handleHide(),
      // Disable right flip when already sent interest (visual flip only, snaps back)
      onFlipRight: iLiked ? undefined : () => handleFavorite(),
    });
    gestureCleanups.push(flipCleanup);
  }

  // Keyboard: navigate between profiles (if browse context exists)
  if (hasBrowseContext) {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!document.querySelector('.pub-wrapper')) return;
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateToProfile(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateToProfile(1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    gestureCleanups.push(() => document.removeEventListener('keydown', handleKeyDown));
  }
}

/**
 * Render navigation UI for browse context
 */
function renderNavigationUI(): string {
  const context = store.getBrowseContext();
  if (!context || context.profiles.length <= 1) return '';

  return `
    <button class="pub-nav-btn pub-nav-prev" id="nav-prev-btn" title="Previous (Left arrow)">
      <i class="fas fa-chevron-left"></i>
    </button>
    <button class="pub-nav-btn pub-nav-next" id="nav-next-btn" title="Next (Right arrow)">
      <i class="fas fa-chevron-right"></i>
    </button>
  `;
}

/**
 * Setup navigation button handlers
 */
function setupNavigationButtons(): void {
  document.getElementById('nav-prev-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    navigateToProfile(-1);
  });
  document.getElementById('nav-next-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    navigateToProfile(1);
  });
}

// Build location string - show only the most specific: city > state > country
function formatLocation(city?: string | null, state?: string | null, country?: string | null): string {
  if (city) return city;
  if (state) return state;
  if (country) return country;
  return '';
}

// Render a section with title and content
function renderSection(title: string, icon: string, content: string): string {
  if (!content.trim()) return '';
  return `
    <div class="pub-section">
      <div class="pub-section-header">
        <i class="fas fa-${icon}"></i>
        <span>${title}</span>
      </div>
      <div class="pub-section-content">
        ${content}
      </div>
    </div>
  `;
}

// Render search preview section (shows how profile appears in different grid sizes)
function renderSearchPreview(profile: PublicProfileResponse): string {
  // Cast to BrowseProfile type (safe - all required fields present)
  const browseProfile: BrowseProfile = {
    user_id: profile.user_id,
    first_name: profile.first_name,
    age: profile.age,
    gender: profile.gender,
    relationship_status: profile.relationship_status,
    bio: profile.bio,
    city: profile.city,
    state: profile.state,
    country: profile.country,
    school: profile.school,
    company: profile.company,
    job_title: profile.job_title,
    height_cm: profile.height_cm,
    photos: profile.photos,
    profile_json: profile.profile_json,
    user_type: profile.user_type,
  };

  const cardHtml = renderBrowseCard(browseProfile);

  return `
    <div class="pub-preview-separator"></div>
    <div class="pub-section pub-preview-section">
      <div class="pub-section-header">
        <i class="fas fa-eye"></i>
        <span>How you appear in search results</span>
      </div>
      <div class="pub-section-content">
        <div class="pub-preview-explainer">
          Your profile appears in different sizes depending on the grid view others choose:
        </div>

        <div class="pub-preview-stack">
          <!-- Large (1-column) preview -->
          <div class="pub-preview-item">
            <div class="pub-preview-label">Large (1 column)</div>
            <div class="pub-preview-card pub-preview-card-large">
              ${cardHtml}
            </div>
          </div>

          <!-- Medium (3-column) preview -->
          <div class="pub-preview-item">
            <div class="pub-preview-label">Medium (3 columns)</div>
            <div class="pub-preview-card pub-preview-card-medium">
              ${cardHtml}
            </div>
          </div>

          <!-- Small (5-column) preview -->
          <div class="pub-preview-item">
            <div class="pub-preview-label">Small (5 columns)</div>
            <div class="pub-preview-card pub-preview-card-small browse-grid-compact">
              ${cardHtml}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Render info row
function renderInfoRow(icon: string, label: string, value: string | undefined | null): string {
  if (!value) return '';
  return `
    <div class="pub-info-row">
      <i class="fas fa-${icon}"></i>
      <span class="pub-info-label">${label}</span>
      <span class="pub-info-value">${value}</span>
    </div>
  `;
}

// Render children details as indented sub-rows
function renderChildrenDetails(children: Array<{ birthday?: string; gender?: string; lives_with_me?: number }>): string {
  if (!children || children.length === 0) return '';

  const rows = children.map(child => {
    const parts: string[] = [];

    if (child.gender && child.gender !== 'unspecified') {
      parts.push(CHILD_GENDER_LABELS[child.gender] || child.gender);
    }

    if (child.birthday) {
      const age = calculateAge(child.birthday);
      if (age !== null) {
        const wholeAge = Math.floor(age);
        parts.push(wholeAge < 1 ? 'Under 1' : `${wholeAge}`);
      }
    }

    if (child.lives_with_me !== undefined && child.lives_with_me !== null) {
      if (child.lives_with_me === 100) parts.push('Full time');
      else if (child.lives_with_me === 0) parts.push('No custody');
      else parts.push(`${child.lives_with_me}%`);
    }

    if (parts.length === 0) return '';
    return `<div class="pub-child-row"><i class="fas fa-child"></i><span>${parts.join(' \u00b7 ')}</span></div>`;
  }).filter(Boolean).join('');

  return rows ? `<div class="pub-children-list">${rows}</div>` : '';
}

// Format a single intent entry for display
function formatIntentEntry(entry: IntentEntry): string {
  const parts: string[] = [];

  // Intents (e.g. "Dating, Long-term")
  if (entry.intents?.length) {
    parts.push(entry.intents.map(i => RELATIONSHIP_INTENT_LABELS[i] || formatLabel(i)).join(', '));
  }

  // Genders (e.g. "with Women" if intents precede, else just "Women")
  if (entry.genders?.length) {
    const genderLabels = entry.genders.map(g => INTERESTED_IN_LABELS[g] || formatLabel(g)).join(', ');
    parts.push(parts.length ? `with ${genderLabels}` : genderLabels);
  }

  return parts.join(' ');
}

// Interest icons mapping (FontAwesome icon names)
const interestIcons: Record<string, string> = {
  music: 'music',
  hiking: 'hiking',
  photography: 'camera',
  cooking: 'utensils',
  travel: 'plane',
  reading: 'book',
  gaming: 'gamepad',
  fitness: 'dumbbell',
  art: 'palette',
  movies: 'film',
  sports: 'football-ball',
  yoga: 'spa',
  dancing: 'music',
  writing: 'pen',
  gardening: 'seedling',
  pets: 'paw',
  nature: 'tree',
  technology: 'laptop',
  fashion: 'tshirt',
  wine: 'wine-glass-alt',
  coffee: 'coffee',
  outdoors: 'mountain',
  running: 'running',
  cycling: 'bicycle',
  swimming: 'swimmer',
  meditation: 'om',
  crafts: 'cut',
  theater: 'theater-masks',
  comedy: 'laugh',
  anime: 'dragon',
  podcasts: 'podcast',
  volunteering: 'hands-helping',
  languages: 'language',
  history: 'landmark',
  science: 'flask',
  default: 'star',
};

// Get icon for interest (case-insensitive matching)
function getInterestIcon(interest: string): string {
  const key = interest.toLowerCase().trim();
  // Check for exact match first
  if (interestIcons[key]) return interestIcons[key];
  // Check if interest contains a keyword
  for (const [keyword, icon] of Object.entries(interestIcons)) {
    if (key.includes(keyword) || keyword.includes(key)) {
      return icon;
    }
  }
  return interestIcons.default;
}

// Render interest grid with icons
function renderInterestGrid(interests: string[]): string {
  if (!interests.length) return '';
  return `
    <div class="pub-interest-grid">
      ${interests
        .map(
          (interest) => `
        <div class="pub-interest-card">
          <i class="fas fa-${getInterestIcon(interest)}"></i>
          <span>${interest}</span>
        </div>
      `
        )
        .join('')}
    </div>
  `;
}

// Lifestyle meter percentages
const lifestylePercentages: Record<string, number> = {
  never: 0,
  rarely: 33,
  sometimes: 66,
  often: 100,
  daily: 100,
};

// Render lifestyle meter with visual bar
function renderLifestyleMeter(label: string, value: string | undefined, formatter: (v: string) => string = formatFrequency): string {
  if (!value) return '';
  const display = formatter(value);
  const percentage = lifestylePercentages[value] ?? 50;
  return `
    <div class="pub-lifestyle-meter">
      <span class="pub-meter-label">${label}</span>
      <div class="pub-meter-track">
        <div class="pub-meter-fill" style="width: ${percentage}%"></div>
      </div>
      <span class="pub-meter-value">${display}</span>
    </div>
  `;
}

// Render inline action buttons for all 4 interaction states
function renderInlineActions(matchInfo: { matched: boolean; matched_at: string | null; chat_id: string | null; i_liked: boolean; they_liked: boolean }, suffix: string): string {
  const isMatched = matchInfo.matched;
  const iSent = matchInfo.i_liked && !isMatched;

  // Check button: class and label depend on state
  let checkClass = 'pub-action-favorite';
  let checkLabel = 'Interested';
  let checkDisabled = false;

  if (isMatched) {
    checkClass = 'pub-action-btn pub-action-matched';
    checkLabel = 'Matched';
    checkDisabled = true;
  } else if (iSent) {
    checkClass = 'pub-action-btn pub-action-sent';
    checkLabel = 'Interest sent';
    checkDisabled = true;
  }

  // Button label changes when matched
  const passLabel = isMatched ? 'Unmatch' : 'Not for me';

  let html = `<div class="pub-inline-actions"><div class="pub-action-buttons">`;

  // Pass / Unmatch button (always active)
  html += `
    <div class="pub-action-group">
      <button class="btn-round pub-action-btn pub-action-hide inline-action-hide" data-suffix="${suffix}" title="${passLabel}">
        <i class="fas fa-times"></i>
      </button>
      <span class="pub-action-label">${passLabel}</span>
    </div>`;

  // Check / Interested / Sent / Matched button
  html += `
    <div class="pub-action-group">
      <button class="btn-round pub-action-btn ${checkClass} inline-action-favorite" data-suffix="${suffix}" title="${checkLabel}"${checkDisabled ? ' disabled' : ''}>
        <i class="fas fa-check"></i>
      </button>
      <span class="pub-action-label">${checkLabel}</span>
    </div>`;

  // Chat button (only when matched)
  if (isMatched) {
    html += `
      <div class="pub-action-group">
        <button class="btn-round pub-action-btn pub-action-chat inline-action-chat" title="Chat">
          <i class="fas fa-comment"></i>
        </button>
        <span class="pub-action-label">Chat</span>
      </div>`;
  }

  html += `</div>`; // close .pub-action-buttons

  // Match date below buttons
  if (isMatched && matchInfo.matched_at) {
    html += `<span class="pub-match-date">Matched ${formatFriendlyDate(matchInfo.matched_at)}</span>`;
  }

  html += `</div>`; // close .pub-inline-actions

  return html;
}

export async function renderProfileView(): Promise<void> {
  const params = getRouteParams();
  const userId = params.id;
  currentUserId = userId;
  interactionInFlight = false;

  // Check if viewing own profile (route param can be a user_id or handle)
  const myUser = store.getState().user;
  const isOwnProfile = userId === myUser?.id ||
    (!!myUser?.profile?.handle && userId.toLowerCase() === myUser.profile.handle.toLowerCase());

  // Show loading initially
  render(
    `
    <div class="pub-wrapper">
      <div class="pub-loading">
        ${loadingSpinner()}
      </div>
    </div>
  `,
    false
  );

  if (!userId) {
    render(
      `
      <div class="pub-wrapper">
        ${emptyState({
          icon: 'user-slash',
          title: 'No user ID provided',
          message: 'Unable to load profile',
        })}
      </div>
    `,
      false
    );
    return;
  }

  try {
    // Fetch profile and match status in parallel
    const [profileResponse, matchResponse] = await Promise.all([
      profileApi.getById(userId),
      !isOwnProfile ? matchesApi.checkMatch(userId) : Promise.resolve({ data: { matched: false, matched_at: null, chat_id: null, i_liked: false, they_liked: false } }),
    ]);

    const profile = profileResponse.data;
    const matchInfo = matchResponse.data;

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Extract data
    const displayAge = profile.age || null;
    currentFirstName = profile.first_name || 'Unknown';
    const photos = Array.isArray(profile.photos) ? profile.photos : [];
    const primaryPhoto = photos[0];
    const additionalPhotos = photos.slice(1);
    const pj: ProfileJson = profile.profile_json || {};

    // Location with distance (filter PNTA values)
    const city = filterPnta(profile.city);
    const state = filterPnta(profile.state);
    const country = filterPnta(profile.country);
    const baseLocation = formatLocation(city, state, country);

    // Get distance from browse context or matches cache
    const browseContext = store.getBrowseContext();
    const browseProfile = browseContext?.profiles.find(p => p.user_id === userId);
    const distanceKm = browseProfile?.distance_km ?? store.getDistance(userId!);
    const units = getUnits();

    // Build location with distance
    let location = baseLocation;
    if (baseLocation && distanceKm != null) {
      location = `${baseLocation} (${formatDistance(distanceKm, units)})`;
    } else if (!baseLocation && distanceKm != null) {
      location = formatDistance(distanceKm, units);
    }

    // Set module-level matched flag for unmatch confirmation
    currentIsMatched = matchInfo.matched;

    // Build hero badge based on interaction state
    let heroBadge = '';
    if (matchInfo.matched) {
      heroBadge = `<span class="pub-status-badge pub-status-matched"><i class="fas fa-check"></i> Matched</span>`;
    } else if (matchInfo.i_liked && !matchInfo.matched) {
      heroBadge = `<span class="pub-status-badge pub-status-sent"><i class="fas fa-check"></i> Interest sent</span>`;
    } else if (matchInfo.they_liked && !matchInfo.i_liked) {
      heroBadge = `<span class="pub-status-badge pub-status-interested"><i class="fas fa-check"></i> They're interested</span>`;
    }

    // Build headline (filter PNTA)
    const headline = filterPnta(pj.about?.headline) || '';

    // Build interests (filter PNTA)
    const rawInterests = Array.isArray(pj.about?.interests) ? pj.about.interests : [];
    const interests = filterPnta(rawInterests) as string[] || [];

    // Build basics section
    let basicsContent = '';
    basicsContent += renderInfoRow('user', 'Name', currentFirstName);
    if (profile.gender) {
      basicsContent += renderInfoRow('venus-mars', 'Gender', profile.gender);
    }
    const filteredOrientation = filterPnta(pj.identity?.sexual_orientation) as string[] | null;
    if (filteredOrientation?.length) {
      basicsContent += renderInfoRow(
        'compass',
        'Orientation',
        filteredOrientation.map(formatLabel).join(', ')
      );
    }
    if (profile.height_cm) {
      basicsContent += renderInfoRow('ruler-vertical', 'Height', formatHeight(profile.height_cm, units));
    }
    if (pj.body?.weight_lb) {
      basicsContent += renderInfoRow('weight', 'Weight', formatWeight(pj.body.weight_lb, units));
    }
    const filteredBodyType = filterPnta(pj.body?.body_type);
    if (filteredBodyType) {
      basicsContent += renderInfoRow('male', 'Body Type', formatBodyType(filteredBodyType));
    }
    const filteredEthnicity = filterPnta(pj.identity?.ethnicity) as string[] | null;
    if (filteredEthnicity?.length) {
      basicsContent += renderInfoRow('globe', 'Ethnicity', formatEthnicities(filteredEthnicity));
    }
    const filteredLanguages = filterPnta(pj.identity?.languages) as string[] | null;
    if (filteredLanguages?.length) {
      basicsContent += renderInfoRow('language', 'Languages', filteredLanguages.map(formatLanguage).join(', '));
    }
    // Location row: "City, State" or just country if no city
    const locParts = [city, state].filter(Boolean);
    const basicLocation = locParts.length ? locParts.join(', ') : (country || '');
    if (basicLocation) {
      basicsContent += renderInfoRow('map-marker-alt', 'Location', basicLocation);
    }
    const filteredHometown = filterPnta(pj.identity?.hometown);
    if (filteredHometown) {
      basicsContent += renderInfoRow('home', 'Hometown', filteredHometown);
    }

    // Build identity section
    let identityContent = '';
    const filteredPronouns = filterPnta(pj.identity?.pronouns) as string[] | null;
    if (filteredPronouns?.length) {
      identityContent += renderInfoRow('comment', 'Pronouns', formatPronouns(filteredPronouns, pj.identity?.pronouns_custom));
    }
    const filteredReligion = filterPnta(pj.identity?.religion);
    if (filteredReligion) {
      identityContent += renderInfoRow('pray', 'Religion', formatLabel(filteredReligion));
    }
    const filteredPoliticalView = filterPnta(pj.identity?.political_view);
    if (filteredPoliticalView) {
      identityContent += renderInfoRow('landmark', 'Politics', formatLabel(filteredPoliticalView));
    }
    const filteredZodiac = filterPnta(pj.identity?.zodiac_sign);
    if (filteredZodiac) {
      identityContent += renderInfoRow('star', 'Star Sign', formatZodiacSign(filteredZodiac));
    }

    // Build work/education section (filter PNTA values)
    let workContent = '';
    const filteredJobTitle = filterPnta(profile.job_title);
    const filteredCompany = filterPnta(profile.company);
    if (filteredJobTitle) {
      workContent += renderInfoRow('briefcase', 'Job Title', filteredJobTitle);
    }
    if (filteredCompany) {
      workContent += renderInfoRow('building', 'Where I Work', filteredCompany);
    }
    const filteredCareerField = filterPnta(pj.background?.career_field);
    if (filteredCareerField) {
      workContent += renderInfoRow('industry', 'Field', formatLabel(filteredCareerField));
    }
    const filteredEmployment = filterPnta(pj.background?.employment_status);
    if (filteredEmployment) {
      workContent += renderInfoRow('clock', 'Employment', formatEmployment(filteredEmployment));
    }
    const filteredSchool = filterPnta(profile.school);
    if (filteredSchool) {
      workContent += renderInfoRow('graduation-cap', 'School', filteredSchool);
    }
    const filteredEducation = filterPnta(pj.background?.education_level);
    if (filteredEducation) {
      workContent += renderInfoRow('university', 'Education', formatEducation(filteredEducation));
    }
    const filteredMajor = filterPnta(pj.background?.major);
    if (filteredMajor) {
      workContent += renderInfoRow('book', 'Major', filteredMajor);
    }
    const filteredIncome = filterPnta(pj.background?.income_range);
    if (filteredIncome) {
      workContent += renderInfoRow('dollar-sign', 'Income', formatIncome(filteredIncome));
    }

    // Build lifestyle section
    let lifestyleContent = '';
    if (pj.lifestyle) {
      lifestyleContent += renderLifestyleMeter('Drinking', filterPnta(pj.lifestyle.drinking) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Smoking', filterPnta(pj.lifestyle.smoking) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Cannabis', filterPnta(pj.lifestyle.cannabis) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Drugs', filterPnta(pj.lifestyle.drugs) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Exercise', filterPnta(pj.lifestyle.exercise) ?? undefined, formatExercise);
    }

    // Build family section
    let familyContent = '';
    const filteredHasKids = filterPnta(pj.kids?.has_kids);
    if (filteredHasKids) {
      let kidsGroup = renderInfoRow('child', 'Kids', formatKidsStatus(filteredHasKids));
      if (pj.kids?.children?.length) {
        kidsGroup += renderChildrenDetails(pj.kids.children);
      }
      familyContent += `<div class="pub-kids-group">${kidsGroup}</div>`;
    }
    const filteredWantsMoreKids = filterPnta(pj.kids?.wants_more_kids);
    if (filteredWantsMoreKids) {
      familyContent += renderInfoRow('baby', 'Family Plans', formatWantsKids(filteredWantsMoreKids, pj.kids?.has_kids));
    }
    const filteredPetsHas = filterPnta(pj.pets?.has);
    if (filteredPetsHas) {
      const hasPets = formatPetsList(filteredPetsHas);
      if (hasPets && hasPets !== 'None') {
        familyContent += renderInfoRow('paw', 'Pets', hasPets);
      }
    }
    const filteredPetsWants = filterPnta(pj.pets?.wants);
    if (filteredPetsWants) {
      const wantsPets = formatPetsList(filteredPetsWants);
      if (wantsPets && wantsPets !== 'None') {
        familyContent += renderInfoRow('bone', 'Wants Pets', wantsPets);
      }
    }

    // Build connections section (filter PNTA values)
    let connectionContent = '';
    if (profile.relationship_status) {
      connectionContent += renderInfoRow('ring', 'Status', profile.relationship_status);
    }
    // Intent entries (one row per entry)
    const intentEntries = pj.preferences?.intent_entries as IntentEntry[] | undefined;
    if (intentEntries?.length) {
      intentEntries.forEach(entry => {
        connectionContent += renderInfoRow('search', 'Looking For', formatIntentEntry(entry));
      });
    }
    // Structure
    const filteredStructure = filterPnta(pj.relationship?.structure);
    if (filteredStructure) {
      connectionContent += renderInfoRow('users', 'Structure', formatRelationshipStructure(filteredStructure));
    }
    if (pj.relationship?.open_to_relocation && pj.relationship.open_to_relocation !== 'pnta') {
      connectionContent += renderInfoRow(
        'plane',
        'Will Relocate',
        formatFlexibility(pj.relationship.open_to_relocation)
      );
    }
    if (pj.relationship?.open_to_long_distance && pj.relationship.open_to_long_distance !== 'pnta') {
      connectionContent += renderInfoRow(
        'map-marker-alt',
        'Long Distance',
        formatFlexibility(pj.relationship.open_to_long_distance)
      );
    }
    // Show disclosures only if true (Yes)
    if (pj.disclosures?.inclusive_of_trans_nb) {
      connectionContent += renderInfoRow('rainbow', 'Trans-inclusive', 'Yes');
    }
    if (pj.disclosures?.open_to_enm_poly) {
      connectionContent += renderInfoRow('users', 'Open to ENM/poly', 'Yes');
    }

    // Build the full page
    const html = `
      <div class="pub-wrapper">
        <!-- Back button overlay -->
        <button class="btn-back pub-close-btn pub-close-btn--left" id="close-profile-btn">
          <i class="fas fa-arrow-left"></i>
        </button>

        <!-- Navigation UI (only shown when coming from browse) -->
        ${renderNavigationUI()}

        <!-- Hero section with photo -->
        <div class="pub-hero">
          ${
            primaryPhoto && primaryPhoto.files
              ? `<img src="${primaryPhoto.files.square_large?.url || primaryPhoto.files.large?.url || primaryPhoto.files.medium?.url || primaryPhoto.files.original?.url || ''}" alt="${currentFirstName}" class="pub-hero-img" onerror="this.style.display='none'" />`
              : `<div class="pub-hero-placeholder"><i class="fas fa-user"></i></div>`
          }
          <div class="pub-hero-overlay">
            <h1 class="pub-hero-name">${currentFirstName}${heroBadge}</h1>
            <div class="pub-hero-details">
              <span>${[displayAge, location].filter(Boolean).join(' · ')}</span>
              ${profile.user_type === 1 ? '<span class="ai-badge"></span>' : ''}
            </div>
          </div>
        </div>

        <!-- Scrollable content -->
        <div class="pub-content">
          ${!isOwnProfile ? renderInlineActions(matchInfo, 'top') : ''}

          ${headline ? `<div class="pub-headline">${headline}</div>` : ''}

          ${filterPnta(profile.bio) ? `<div class="pub-bio">${filterPnta(profile.bio)}</div>` : ''}

          ${basicsContent ? renderSection('Basics', 'id-card', `<div class="pub-info-grid">${basicsContent}</div>`) : ''}

          ${connectionContent ? renderSection('Looking For', 'search', `<div class="pub-info-grid">${connectionContent}</div>`) : ''}

          ${interests.length ? renderSection('Interests', 'star', renderInterestGrid(interests)) : ''}

          ${!isOwnProfile ? renderInlineActions(matchInfo, 'mid') : ''}

          ${familyContent ? renderSection('Family & Pets', 'home', `<div class="pub-info-grid">${familyContent}</div>`) : ''}

          ${identityContent ? renderSection('Identity', 'user-circle', `<div class="pub-info-grid">${identityContent}</div>`) : ''}

          ${lifestyleContent ? renderSection('Lifestyle', 'wine-glass-alt', `<div class="pub-lifestyle-meters">${lifestyleContent}</div>`) : ''}

          ${workContent ? renderSection('Work & Education', 'briefcase', `<div class="pub-info-grid">${workContent}</div>`) : ''}

          ${
            additionalPhotos.length
              ? additionalPhotos
                  .map(
                    (photo: any) => `
              <div class="pub-photo-block">
                <img src="${photo.files.square_large?.url || photo.files.large?.url || photo.files.medium?.url}" alt="Photo" />
                ${photo.caption_user || photo.caption_ai ? `<div class="pub-photo-caption">${photo.caption_user || photo.caption_ai}</div>` : ''}
              </div>
            `
                  )
                  .join('')
              : ''
          }

          ${!isOwnProfile ? renderInlineActions(matchInfo, 'bottom') : ''}

          ${isOwnProfile ? renderSearchPreview(profile) : ''}

          <div class="pub-safety-row">
            <button class="pub-safety-btn-inline" id="safety-btn" title="Report or Block">
              <i class="fas fa-shield-alt"></i>
            </button>
          </div>
        </div>
      </div>
    `;

    render(html, false);

    // Setup event handlers
    setupProfileCloseButton();

    // Safety button works for all profiles (disabled for own profile)
    const isAdmin = !!myUser?.email?.endsWith('@914-6.com');
    document.getElementById('safety-btn')?.addEventListener('click', () => {
      if (!currentUserId) return;
      const ctx = store.getBrowseContext();
      showSafetyActionSheet(currentUserId, currentFirstName, {
        onBlocked: isOwnProfile ? undefined : () => navigate(ctx?.returnTo || '/browse'),
        onDeleted: isOwnProfile ? undefined : () => navigate(ctx?.returnTo || '/browse'),
        isSelf: isOwnProfile,
        isAdmin,
      });
    });

    // Photo click-to-view works for all profiles (own + others)
    setupPhotoClickHandlers();

    if (!isOwnProfile) {
      // Wire up hide/favorite buttons for all states
      setupInlineActionButtons();

      // Wire up chat buttons when matched
      if (matchInfo.matched) {
        document.querySelectorAll('.inline-action-chat').forEach(btn => {
          btn.addEventListener('click', () => {
            if (matchInfo.chat_id) {
              navigate(`/chat/${matchInfo.chat_id}`);
            } else {
              navigate('/matches');
            }
          });
        });
      }

      // Setup gestures (double-tap on images, swipe for like/pass)
      setupGestures(matchInfo.matched, matchInfo.chat_id ?? null, matchInfo.i_liked);
      setupNavigationButtons();
    }
  } catch (err: any) {
    render(
      `
      <div class="pub-wrapper">
        <button class="btn-back pub-close-btn pub-close-btn--left" id="close-profile-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
        ${emptyState({
          icon: 'user-slash',
          title: 'Profile not found',
          message: err.message || 'Unable to load this profile',
          buttonText: 'Go Back',
          buttonId: 'go-back-btn',
        })}
      </div>
    `,
      false
    );
    setupProfileCloseButton();
    document.getElementById('go-back-btn')?.addEventListener('click', () => navigate('/'));
  }
}

function setupInlineActionButtons(): void {
  document.querySelectorAll('.inline-action-favorite').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!currentUserId || (btn as HTMLButtonElement).disabled) return;
      handleFavorite();
    });
  });

  document.querySelectorAll('.inline-action-hide').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!currentUserId) return;
      handleHide();
    });
  });
}

function disableAllActionButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('.inline-action-btn, .inline-action-favorite, .inline-action-hide, .inline-action-chat').forEach(btn => {
    btn.disabled = true;
  });
}

function enableAllActionButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('.inline-action-btn, .inline-action-favorite, .inline-action-hide, .inline-action-chat').forEach(btn => {
    btn.disabled = false;
  });
}

async function handleFavorite(): Promise<void> {
  if (!currentUserId || interactionInFlight) return;
  interactionInFlight = true;

  disableAllActionButtons();

  try {
    const response = await searchApi.interact(currentUserId, 1);

    // Refresh likes count (non-blocking)
    refreshLikesCount();

    if (response.data.isMatch) {
      showMatchModal(currentFirstName, response.data.chatId);
    } else {
      showToast(TOAST_INTEREST_SENT, 'success');
      navigate('/');
    }
  } catch (err: any) {
    showApiError(err, API_ERR_SEND_INTEREST);
    enableAllActionButtons();
  } finally {
    interactionInFlight = false;
  }
}

async function handleHide(): Promise<void> {
  if (!currentUserId || interactionInFlight) return;

  // Show confirmation modal when unmatching
  if (currentIsMatched) {
    const confirmed = await confirmUnmatch(currentFirstName);
    if (!confirmed) return;
  }

  interactionInFlight = true;
  disableAllActionButtons();

  try {
    await searchApi.interact(currentUserId, SKIP_SCORE);
    // Refresh likes count (non-blocking)
    refreshLikesCount();
    showToast(currentIsMatched ? toastUnmatched(currentFirstName) : toastProfileHidden(), 'success');
    if (currentIsMatched) {
      navigate('/matches');
    } else {
      navigate('/');
    }
  } catch (err: any) {
    showApiError(err, currentIsMatched ? API_ERR_UNMATCH : API_ERR_HIDE_PROFILE);
    enableAllActionButtons();
  } finally {
    interactionInFlight = false;
  }
}

function confirmUnmatch(name: string): Promise<boolean> {
  return new Promise((resolve) => {
    const modalHtml = `
      <div class="modal-overlay" id="unmatch-modal">
        <div class="modal-content">
          <h3>Unmatch ${name}?</h3>
          <p>This will remove your match and hide them from your matches list.</p>
          <div class="modal-buttons">
            <button class="btn btn-secondary" id="unmatch-cancel-btn">Cancel</button>
            <button class="btn btn-danger" id="unmatch-confirm-btn">Unmatch</button>
          </div>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const cleanup = () => document.getElementById('unmatch-modal')?.remove();

    document.getElementById('unmatch-cancel-btn')?.addEventListener('click', () => {
      cleanup();
      resolve(false);
    });

    document.getElementById('unmatch-confirm-btn')?.addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    document.getElementById('unmatch-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'unmatch-modal') {
        cleanup();
        resolve(false);
      }
    });
  });
}

function showMatchModal(name: string, chatId?: string): void {
  const modalHtml = `
    <div class="modal-overlay" id="match-modal">
      <div class="modal-content match-modal">
        <div class="match-celebration">
          <i class="fas fa-circle-check"></i>
        </div>
        <h3>Connected</h3>
        <p>You and ${name} expressed mutual interest</p>
        <div class="modal-buttons">
          <button class="btn btn-secondary" id="match-later-btn">Later</button>
          <button class="btn btn-primary" id="match-chat-btn">Say Hi</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  document.getElementById('match-later-btn')?.addEventListener('click', () => {
    closeMatchModal();
    navigate('/browse');
  });

  document.getElementById('match-chat-btn')?.addEventListener('click', () => {
    closeMatchModal();
    if (chatId) {
      navigate(`/chat/${chatId}`);
    } else {
      navigate('/matches');
    }
  });

  document.getElementById('match-modal')?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === 'match-modal') {
      closeMatchModal();
      navigate('/browse');
    }
  });
}

function closeMatchModal(): void {
  document.getElementById('match-modal')?.remove();
}

/**
 * Render own profile as an inline tab panel (reuses public profile helpers).
 * Fetches the user's public profile, builds the HTML, injects into container.
 * Skips: nav buttons, action buttons, safety row, gestures, page-flip.
 */
export async function renderOwnProfileTab(container: HTMLElement): Promise<void> {
  const myUser = store.getState().user;
  if (!myUser) return;

  container.innerHTML = `<div class="pub-loading">${loadingSpinner()}</div>`;

  try {
    const profileResponse = await profileApi.getById(myUser.id);
    const profile = profileResponse.data;
    if (!profile) throw new Error('Profile not found');

    const firstName = profile.first_name || 'Unknown';
    const displayAge = profile.age || null;
    const photos = Array.isArray(profile.photos) ? profile.photos : [];
    const primaryPhoto = photos[0];
    const additionalPhotos = photos.slice(1);
    const pj: ProfileJson = profile.profile_json || {};

    // Location
    const city = filterPnta(profile.city);
    const state = filterPnta(profile.state);
    const country = filterPnta(profile.country);
    const location = formatLocation(city, state, country);

    const headline = filterPnta(pj.about?.headline) || '';
    const rawInterests = Array.isArray(pj.about?.interests) ? pj.about.interests : [];
    const interests = filterPnta(rawInterests) as string[] || [];

    // Basics
    let basicsContent = '';
    basicsContent += renderInfoRow('user', 'Name', firstName);
    if (profile.gender) basicsContent += renderInfoRow('venus-mars', 'Gender', profile.gender);
    const filteredOrientation = filterPnta(pj.identity?.sexual_orientation) as string[] | null;
    if (filteredOrientation?.length) basicsContent += renderInfoRow('compass', 'Orientation', filteredOrientation.map(formatLabel).join(', '));
    const units = getUnits();
    if (profile.height_cm) basicsContent += renderInfoRow('ruler-vertical', 'Height', formatHeight(profile.height_cm, units));
    if (pj.body?.weight_lb) basicsContent += renderInfoRow('weight', 'Weight', formatWeight(pj.body.weight_lb, units));
    const filteredBodyType = filterPnta(pj.body?.body_type);
    if (filteredBodyType) basicsContent += renderInfoRow('male', 'Body Type', formatBodyType(filteredBodyType));
    const filteredEthnicity = filterPnta(pj.identity?.ethnicity) as string[] | null;
    if (filteredEthnicity?.length) basicsContent += renderInfoRow('globe', 'Ethnicity', formatEthnicities(filteredEthnicity));
    const filteredLanguages = filterPnta(pj.identity?.languages) as string[] | null;
    if (filteredLanguages?.length) basicsContent += renderInfoRow('language', 'Languages', filteredLanguages.map(formatLanguage).join(', '));
    const locParts = [city, state].filter(Boolean);
    const basicLocation = locParts.length ? locParts.join(', ') : (country || '');
    if (basicLocation) basicsContent += renderInfoRow('map-marker-alt', 'Location', basicLocation);
    const filteredHometown = filterPnta(pj.identity?.hometown);
    if (filteredHometown) basicsContent += renderInfoRow('home', 'Hometown', filteredHometown);

    // Identity
    let identityContent = '';
    const filteredPronouns = filterPnta(pj.identity?.pronouns) as string[] | null;
    if (filteredPronouns?.length) identityContent += renderInfoRow('comment', 'Pronouns', formatPronouns(filteredPronouns, pj.identity?.pronouns_custom));
    const filteredReligion = filterPnta(pj.identity?.religion);
    if (filteredReligion) identityContent += renderInfoRow('pray', 'Religion', formatLabel(filteredReligion));
    const filteredPoliticalView = filterPnta(pj.identity?.political_view);
    if (filteredPoliticalView) identityContent += renderInfoRow('landmark', 'Politics', formatLabel(filteredPoliticalView));
    const filteredZodiac = filterPnta(pj.identity?.zodiac_sign);
    if (filteredZodiac) identityContent += renderInfoRow('star', 'Star Sign', formatZodiacSign(filteredZodiac));

    // Work & Education
    let workContent = '';
    const filteredJobTitle = filterPnta(profile.job_title);
    if (filteredJobTitle) workContent += renderInfoRow('briefcase', 'Job Title', filteredJobTitle);
    const filteredCompany = filterPnta(profile.company);
    if (filteredCompany) workContent += renderInfoRow('building', 'Where I Work', filteredCompany);
    const filteredCareerField = filterPnta(pj.background?.career_field);
    if (filteredCareerField) workContent += renderInfoRow('industry', 'Field', formatLabel(filteredCareerField));
    const filteredEmployment = filterPnta(pj.background?.employment_status);
    if (filteredEmployment) workContent += renderInfoRow('clock', 'Employment', formatEmployment(filteredEmployment));
    const filteredSchool = filterPnta(profile.school);
    if (filteredSchool) workContent += renderInfoRow('graduation-cap', 'School', filteredSchool);
    const filteredEducation = filterPnta(pj.background?.education_level);
    if (filteredEducation) workContent += renderInfoRow('university', 'Education', formatEducation(filteredEducation));
    const filteredMajor = filterPnta(pj.background?.major);
    if (filteredMajor) workContent += renderInfoRow('book', 'Major', filteredMajor);
    const filteredIncome = filterPnta(pj.background?.income_range);
    if (filteredIncome) workContent += renderInfoRow('dollar-sign', 'Income', formatIncome(filteredIncome));

    // Lifestyle
    let lifestyleContent = '';
    if (pj.lifestyle) {
      lifestyleContent += renderLifestyleMeter('Drinking', filterPnta(pj.lifestyle.drinking) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Smoking', filterPnta(pj.lifestyle.smoking) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Cannabis', filterPnta(pj.lifestyle.cannabis) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Drugs', filterPnta(pj.lifestyle.drugs) ?? undefined);
      lifestyleContent += renderLifestyleMeter('Exercise', filterPnta(pj.lifestyle.exercise) ?? undefined, formatExercise);
    }

    // Family
    let familyContent = '';
    const filteredHasKids = filterPnta(pj.kids?.has_kids);
    if (filteredHasKids) {
      let kidsGroup = renderInfoRow('child', 'Kids', formatKidsStatus(filteredHasKids));
      if (pj.kids?.children?.length) kidsGroup += renderChildrenDetails(pj.kids.children);
      familyContent += `<div class="pub-kids-group">${kidsGroup}</div>`;
    }
    const filteredWantsMoreKids = filterPnta(pj.kids?.wants_more_kids);
    if (filteredWantsMoreKids) familyContent += renderInfoRow('baby', 'Family Plans', formatWantsKids(filteredWantsMoreKids, pj.kids?.has_kids));
    const filteredPetsHas = filterPnta(pj.pets?.has);
    if (filteredPetsHas) {
      const hasPets = formatPetsList(filteredPetsHas);
      if (hasPets && hasPets !== 'None') familyContent += renderInfoRow('paw', 'Pets', hasPets);
    }
    const filteredPetsWants = filterPnta(pj.pets?.wants);
    if (filteredPetsWants) {
      const wantsPets = formatPetsList(filteredPetsWants);
      if (wantsPets && wantsPets !== 'None') familyContent += renderInfoRow('bone', 'Wants Pets', wantsPets);
    }

    // Connections
    let connectionContent = '';
    if (profile.relationship_status) connectionContent += renderInfoRow('ring', 'Status', profile.relationship_status);
    const intentEntries = pj.preferences?.intent_entries as IntentEntry[] | undefined;
    if (intentEntries?.length) {
      intentEntries.forEach(entry => {
        connectionContent += renderInfoRow('search', 'Looking For', formatIntentEntry(entry));
      });
    }
    const filteredStructure = filterPnta(pj.relationship?.structure);
    if (filteredStructure) connectionContent += renderInfoRow('users', 'Structure', formatRelationshipStructure(filteredStructure));
    if (pj.relationship?.open_to_relocation && pj.relationship.open_to_relocation !== 'pnta') {
      connectionContent += renderInfoRow('plane', 'Will Relocate', formatFlexibility(pj.relationship.open_to_relocation));
    }
    if (pj.relationship?.open_to_long_distance && pj.relationship.open_to_long_distance !== 'pnta') {
      connectionContent += renderInfoRow('map-marker-alt', 'Long Distance', formatFlexibility(pj.relationship.open_to_long_distance));
    }
    if (pj.disclosures?.inclusive_of_trans_nb) connectionContent += renderInfoRow('rainbow', 'Trans-inclusive', 'Yes');
    if (pj.disclosures?.open_to_enm_poly) connectionContent += renderInfoRow('users', 'Open to ENM/poly', 'Yes');

    // Build HTML (no back button, no nav, no action buttons, no safety row)
    const html = `
      <div class="pub-wrapper pub-wrapper--inline">
        <div class="pub-hero">
          ${primaryPhoto && primaryPhoto.files
            ? `<img src="${primaryPhoto.files.square_large?.url || primaryPhoto.files.large?.url || primaryPhoto.files.medium?.url || primaryPhoto.files.original?.url || ''}" alt="${firstName}" class="pub-hero-img" onerror="this.style.display='none'" />`
            : `<div class="pub-hero-placeholder"><i class="fas fa-user"></i></div>`
          }
          <div class="pub-hero-overlay">
            <h1 class="pub-hero-name">${firstName}</h1>
            <div class="pub-hero-details">
              <span>${[displayAge, location].filter(Boolean).join(' · ')}</span>
            </div>
          </div>
        </div>

        <div class="pub-content">
          ${headline ? `<div class="pub-headline">${headline}</div>` : ''}
          ${filterPnta(profile.bio) ? `<div class="pub-bio">${filterPnta(profile.bio)}</div>` : ''}
          ${basicsContent ? renderSection('Basics', 'id-card', `<div class="pub-info-grid">${basicsContent}</div>`) : ''}
          ${connectionContent ? renderSection('Looking For', 'search', `<div class="pub-info-grid">${connectionContent}</div>`) : ''}
          ${interests.length ? renderSection('Interests', 'star', renderInterestGrid(interests)) : ''}
          ${familyContent ? renderSection('Family & Pets', 'home', `<div class="pub-info-grid">${familyContent}</div>`) : ''}
          ${identityContent ? renderSection('Identity', 'user-circle', `<div class="pub-info-grid">${identityContent}</div>`) : ''}
          ${lifestyleContent ? renderSection('Lifestyle', 'wine-glass-alt', `<div class="pub-lifestyle-meters">${lifestyleContent}</div>`) : ''}
          ${workContent ? renderSection('Work & Education', 'briefcase', `<div class="pub-info-grid">${workContent}</div>`) : ''}
          ${additionalPhotos.length
            ? additionalPhotos.map((photo: any) => `
              <div class="pub-photo-block">
                <img src="${photo.files.square_large?.url || photo.files.large?.url || photo.files.medium?.url}" alt="Photo" />
                ${photo.caption_user || photo.caption_ai ? `<div class="pub-photo-caption">${photo.caption_user || photo.caption_ai}</div>` : ''}
              </div>
            `).join('')
            : ''
          }
        </div>
      </div>
    `;

    container.innerHTML = html;

    // Setup photo click handlers within this container
    const wrapper = container.querySelector('.pub-wrapper') as HTMLElement;
    if (wrapper) {
      const allImgs: string[] = [];
      const heroImg = wrapper.querySelector('.pub-hero-img') as HTMLImageElement;
      if (heroImg) allImgs.push(heroImg.getAttribute('src')!);
      wrapper.querySelectorAll('.pub-photo-block img').forEach((img) => {
        allImgs.push((img as HTMLImageElement).src);
      });

      if (heroImg) {
        heroImg.style.cursor = 'pointer';
        heroImg.addEventListener('click', () => openImageViewer(allImgs, 0));
      }
      wrapper.querySelectorAll('.pub-photo-block img').forEach((img, i) => {
        (img as HTMLElement).style.cursor = 'pointer';
        img.addEventListener('click', () => openImageViewer(allImgs, i + 1));
      });
    }
  } catch {
    container.innerHTML = `<div class="pub-loading" style="padding:2rem;text-align:center;color:var(--text-muted)">Unable to load profile preview</div>`;
  }
}

